import React, { useState } from 'react';
import { Modal, Button, Form, Alert, Table } from 'react-bootstrap';
import { FaFileImport, FaArrowLeft } from 'react-icons/fa';

function ImportModal({ show, onHide, onImport, availableConnectors = [] }) {
  const [jsonInput, setJsonInput] = useState('');
  const [error, setError] = useState('');
  const [step, setStep] = useState('paste'); // 'paste' | 'map'
  const [parsed, setParsed] = useState(null);
  // connectorMapping: importedConnectorId -> selected client connectorId ('' = unmapped)
  const [connectorMapping, setConnectorMapping] = useState({});

  // Current-client connectors whose system matches the imported connector's system.
  const matchesForSystem = (system) =>
    availableConnectors.filter(
      (c) => (c.system || '').toLowerCase() === (system || '').toLowerCase()
    );

  const reset = () => {
    setJsonInput('');
    setError('');
    setStep('paste');
    setParsed(null);
    setConnectorMapping({});
  };

  const handleClose = () => {
    reset();
    onHide();
  };

  // Send the final import with the resolved connector mapping.
  const doImport = (parsedJson, mappingByImportedId) => {
    const resolved = {};
    (parsedJson.connectors || []).forEach((ic) => {
      const selectedId = mappingByImportedId[ic.connectorId];
      resolved[ic.connectorId] = selectedId
        ? availableConnectors.find((c) => c.connectorId === selectedId) || null
        : null;
    });
    onImport(parsedJson, { connectorMapping: resolved });
    handleClose();
  };

  const handleNext = () => {
    try {
      setError('');
      const parsedJson = JSON.parse(jsonInput);

      if (!parsedJson.steps || !Array.isArray(parsedJson.steps)) {
        setError('Invalid JSON: missing "steps" array');
        return;
      }

      const importedConnectors = Array.isArray(parsedJson.connectors) ? parsedJson.connectors : [];

      // No connectors to map — import straight away (variables are still not
      // carried across, and step connector references are blanked downstream).
      if (importedConnectors.length === 0) {
        doImport(parsedJson, {});
        return;
      }

      // Pre-select a target when there is exactly one same-system connector.
      const initialMapping = {};
      importedConnectors.forEach((ic) => {
        const matches = matchesForSystem(ic.system);
        initialMapping[ic.connectorId] = matches.length === 1 ? matches[0].connectorId : '';
      });

      setParsed(parsedJson);
      setConnectorMapping(initialMapping);
      setStep('map');
    } catch (err) {
      setError(`Invalid JSON: ${err.message}`);
    }
  };

  return (
    <Modal show={show} onHide={handleClose} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          <FaFileImport className="me-2" />
          {step === 'paste' ? 'Import Workflow from JSON' : 'Map Connectors'}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <Alert variant="danger">{error}</Alert>}

        {step === 'paste' && (
          <Form.Group>
            <Form.Label className="small fw-bold">Paste JSON workflow definition:</Form.Label>
            <Form.Control
              as="textarea"
              rows={15}
              value={jsonInput}
              onChange={(e) => setJsonInput(e.target.value)}
              placeholder="Paste your workflow JSON here..."
              style={{ fontFamily: 'monospace', fontSize: '0.85rem' }}
            />
          </Form.Group>
        )}

        {step === 'map' && parsed && (
          <>
            <p className="small text-muted">
              This runbook references the connectors below. Choose one of your client&apos;s
              connectors for each (filtered by system). Steps using an unmapped connector will
              have no connector set. Variables are not imported.
            </p>
            <Table size="sm" className="align-middle">
              <thead>
                <tr>
                  <th className="small">From the import</th>
                  <th className="small">Use your connector</th>
                </tr>
              </thead>
              <tbody>
                {parsed.connectors.map((ic) => {
                  const matches = matchesForSystem(ic.system);
                  return (
                    <tr key={ic.connectorId}>
                      <td>
                        <div className="fw-bold small">
                          {ic.connectorNumber ? `${ic.connectorNumber} · ` : ''}
                          {ic.description || 'Connector'}
                        </div>
                        <div className="text-muted" style={{ fontSize: '0.7rem' }}>
                          System: {ic.system || '—'}
                        </div>
                      </td>
                      <td>
                        <Form.Select
                          size="sm"
                          value={connectorMapping[ic.connectorId] || ''}
                          disabled={matches.length === 0}
                          onChange={(e) =>
                            setConnectorMapping((prev) => ({
                              ...prev,
                              [ic.connectorId]: e.target.value,
                            }))
                          }
                        >
                          <option value="">
                            {matches.length === 0
                              ? `No ${ic.system || ''} connectors in this client`
                              : '— Leave unmapped —'}
                          </option>
                          {matches.map((c) => (
                            <option key={c.connectorId} value={c.connectorId}>
                              {c.connectorNumber} - {c.description}
                            </option>
                          ))}
                        </Form.Select>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        {step === 'paste' && (
          <>
            <Button variant="secondary" size="sm" onClick={handleClose}>
              Cancel
            </Button>
            <Button variant="primary" size="sm" onClick={handleNext} disabled={!jsonInput.trim()}>
              <FaFileImport className="me-1" /> Next
            </Button>
          </>
        )}
        {step === 'map' && (
          <>
            <Button variant="secondary" size="sm" onClick={() => setStep('paste')}>
              <FaArrowLeft className="me-1" /> Back
            </Button>
            <Button variant="primary" size="sm" onClick={() => doImport(parsed, connectorMapping)}>
              <FaFileImport className="me-1" /> Import Workflow
            </Button>
          </>
        )}
      </Modal.Footer>
    </Modal>
  );
}

export default ImportModal;
